import { useState, useRef } from "react";
import { Smile, ImagePlus, Send, Loader2, X, Reply, AtSign } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import EmojiPicker from "@/components/EmojiPicker";
import { uploadImage, mediaUrl } from "@/lib/chatApi";

export default function MessageInput({ onSend, onTyping, replyTo, onCancelReply, compact = false, users = [] }) {
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [suggest, setSuggest] = useState([]);
  const fileRef = useRef(null);
  const typingRef = useRef(null);

  const updateSuggestions = (value) => {
    const m = value.match(/@([A-Za-z0-9_]*)$/);
    if (m) {
      const token = m[1].toLowerCase();
      const list = users
        .filter((u) => u.toLowerCase().startsWith(token))
        .slice(0, 6);
      setSuggest(list);
    } else {
      setSuggest([]);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);
    updateSuggestions(v);
    onTyping(true);
    clearTimeout(typingRef.current);
    typingRef.current = setTimeout(() => onTyping(false), 1500);
  };

  const pickMention = (u) => {
    setText((t) => t.replace(/@([A-Za-z0-9_]*)$/, `@${u} `));
    setSuggest([]);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Hanya file gambar yang diperbolehkan");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setImage(url);
    } catch {
      toast.error("Gagal mengunggah gambar");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!text.trim() && !image) return;
    onSend(text.trim(), image);
    setText("");
    setImage(null);
    setSuggest([]);
    onTyping(false);
    clearTimeout(typingRef.current);
  };

  return (
    <div className={`${compact ? "p-2.5" : "p-3 md:p-4"} border-t border-slate-200 dark:border-slate-800/80 bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl shrink-0 relative`}>
      {suggest.length > 0 && (
        <div
          data-testid="mention-suggestions"
          className="absolute bottom-full left-4 mb-2 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl overflow-hidden z-[10000]"
        >
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-3 pt-2 pb-1 flex items-center gap-1">
            <AtSign className="w-3 h-3" /> Sebut pengguna
          </p>
          {suggest.map((u) => (
            <button
              key={u}
              data-testid={`mention-option-${u}`}
              onClick={() => pickMention(u)}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              @{u}
            </button>
          ))}
        </div>
      )}

      {replyTo && (
        <div
          data-testid="reply-preview-bar"
          className="flex items-center gap-2 mb-2 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800/60 border-l-2 border-indigo-500"
        >
          <Reply className="w-4 h-4 text-indigo-500 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-300">Membalas {replyTo.username}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{replyTo.text || "📷 Gambar"}</p>
          </div>
          <button
            data-testid="cancel-reply-button"
            onClick={onCancelReply}
            className="p-1 rounded-md text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {image && (
        <div className="relative inline-block mb-3">
          <img src={mediaUrl(image)} alt="preview" className="h-24 rounded-xl border border-slate-300 dark:border-slate-700" />
          <button
            data-testid="remove-image-button"
            onClick={() => setImage(null)}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center text-white"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <form onSubmit={submit} className="flex items-end gap-2">
        <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="emoji-picker-toggle"
              className="p-2.5 rounded-xl text-slate-400 hover:text-indigo-500 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
            >
              <Smile className="w-5 h-5" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="z-[10000] p-0 border-0 bg-transparent w-auto">
            <EmojiPicker
              onSelect={(e) => {
                setText((t) => t + e);
                setEmojiOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>

        <button
          type="button"
          data-testid="image-upload-button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="p-2.5 rounded-xl text-slate-400 hover:text-indigo-500 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
        </button>
        <input
          ref={fileRef}
          data-testid="image-upload-input"
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
        />

        <textarea
          data-testid="chat-message-input"
          value={text}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) submit(e);
          }}
          rows={1}
          placeholder="Tulis pesan... gunakan @ untuk menyebut"
          className="flex-1 resize-none max-h-32 px-4 py-3 rounded-2xl bg-slate-100 dark:bg-slate-800/70 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm leading-relaxed"
        />

        <button
          type="submit"
          data-testid="send-message-button"
          disabled={!text.trim() && !image}
          className="p-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white transition-all disabled:opacity-40 shadow-lg shadow-indigo-950/30"
        >
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
}
